/**
 * §L1/§L2 · A cohort's MODALITY options, filtered by the in-person
 * vertical.
 *
 * SINGLE source for the four modalities and which ones are offered: the selectors
 * (create cohort, edit cohort) consume them from here instead of repeating the
 * `<option>` list. With `academy.modality.in_person` off, "in-person" and
 * "hybrid" aren't offered — but a cohort that ALREADY has them keeps showing
 * its value (dataPolicy `preserved`: we don't rewrite history).
 */
import type { Accessor } from "solid-js";
import { createMemo } from "solid-js";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import {
  DEFAULT_COHORT_MODALITY,
  MODALITY_IN_PERSON_KEY,
  modalityNeedsInPerson,
} from "@space/shared";
import { useFeatureEnabled } from "@/features/flags/use-feature-enabled";

export type Modality = "in_person" | "online_live" | "hybrid" | "self_paced";

export interface ModalityOption {
  value: Modality;
  label: MessageDescriptor;
}

/** The domain's four modalities, in online-first presentation order. */
const ALL_MODALITIES: readonly ModalityOption[] = [
  { value: "online_live", label: msg`Online live` },
  { value: "self_paced", label: msg`Self-paced` },
  { value: "in_person", label: msg`In person` },
  { value: "hybrid", label: msg`Hybrid` },
];

/** Normalizes a raw `<select>` value into a valid modality. */
export function asModality(v: string): Modality {
  return v === "in_person" || v === "hybrid" || v === "self_paced" || v === "online_live"
    ? v
    : DEFAULT_COHORT_MODALITY;
}

/**
 * Options offerable in `orgId` (defaults to the active org). `current` is the
 * value the entity already has: if it's in-person/hybrid it's kept in the
 * list even if the vertical is off, so editing ANOTHER field of that
 * cohort doesn't silently rewrite it to online.
 */
export function useModalityOptions(args: {
  orgId?: Accessor<string | undefined>;
  current?: Accessor<string | null | undefined>;
}): Accessor<ModalityOption[]> {
  const orgId = () => args.orgId?.();
  const current = () => args.current?.();
  const inPerson = useFeatureEnabled(MODALITY_IN_PERSON_KEY, { orgId });
  const options = createMemo(() => {
    if (inPerson().enabled) {
      return [...ALL_MODALITIES];
    }
    const kept = current();
    return ALL_MODALITIES.filter((o) => !modalityNeedsInPerson(o.value) || o.value === kept);
  });
  return options;
}
